import { ApolloError } from '@apollo/client'

import type { StatusPillVariant } from '@hivewatch/dark'

import { IncidentQuery } from '@/graphql/generated/operations'
import { IncidentStatus, IncidentType } from '@/graphql/generated/schemas'

const ERROR_CODE_ACCESS_DENIED = 'incident.access.denied'

export const incidentTypesWithoutVideo = [
  IncidentType.DeviceHealth,
  IncidentType.DeviceOffline,
]

export const getOrigin = () =>
  typeof window !== 'undefined' && window.location.origin
    ? window.location.origin
    : ''

export const getTabColors = (isSelected: boolean) => {
  const bgColor = isSelected
    ? 'var(--hw-dark-colors-light-interactive-action-bg-selected, #5f96f5)'
    : 'var(--hw-dark-colors-light-interactive-action-bg, #e3eefb)'
  const hoverBgColor = isSelected
    ? 'var(--hw-dark-colors-light-interactive-action-bg-selected, #5f96f5)'
    : 'var(--hw-dark-colors-light-interactive-action-bg-hover, #1b253f)'
  const iconColor = isSelected
    ? 'var(--hw-dark-colors-light-icon-action-selected, #ffffff)'
    : 'var(--hw-dark-colors-light-icon-action-default, #3d7fce)'
  const hoverIconColor = isSelected
    ? 'var(--hw-dark-colors-light-icon-action-selected, #ffffff)'
    : 'var(--hw-dark-colors-light-icon-action-hover, #5a6c9a)'

  return { bgColor, hoverBgColor, iconColor, hoverIconColor }
}

/**
 * Why "Send to Ontic" is unavailable, as an i18n key for the rebrand surfaces
 * to translate (ENG-32094). Returns null when the action is available and no
 * tooltip should render.
 *
 * The sibling `getOnticTooltipMessage` returns hardcoded English and is kept
 * for the frozen legacy drawer tree; don't add call sites to it.
 */
export const getOnticDisabledTooltipKey = (
  incident: IncidentQuery['incident']
):
  | 'incidents:send_to_ontic_unresolved_hint'
  | 'incidents:send_to_ontic_already_sent_hint'
  | null => {
  if (!incident) return null
  if (incident.status !== IncidentStatus.Resolved) {
    return 'incidents:send_to_ontic_unresolved_hint'
  }
  if (incident.isSentToOntic) {
    return 'incidents:send_to_ontic_already_sent_hint'
  }
  return null
}

export const getOnticTooltipMessage = (
  incident: IncidentQuery['incident']
): string => {
  if (!incident) return ''

  const isResolved = incident.status === IncidentStatus.Resolved

  if (!isResolved) {
    return 'Incident must be resolved to send to Ontic'
  }

  if (incident.isSentToOntic) {
    return 'Incident has already been sent to Ontic'
  }

  return '' // No message needed when button is enabled
}

export function getStatusVariant(status: IncidentStatus): StatusPillVariant {
  switch (status) {
    case IncidentStatus.Active:
      return 'incomplete'
    case IncidentStatus.InProgress:
      return 'in-progress'
    case IncidentStatus.Resolved:
      return 'complete'
    default:
      return 'incomplete'
  }
}

/**
 * Checks if an Apollo error contains the 'incident.access.denied' code.
 *
 * @param error - The ApolloError from a GraphQL query
 * @returns `true` if access is denied, `false` otherwise
 */
export const isAccessDeniedCode = (error: ApolloError) => {
  return (
    error?.graphQLErrors?.some(
      (err) => err.extensions?.code === ERROR_CODE_ACCESS_DENIED
    ) ?? false
  )
}
